import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import useFetchOrganizationApiKey from '../api/useFetchOrganizationApiKey';
import type { OrganizationApiKey } from '../types/Organization';
import usePostOrganizationApiKey from '../api/usePostOrganizationApiKey';
import OrganizationSettingsTabs from '../components/SettingsTabs';
import useFetchOrganizationCandidateProfilesCustomFields from '../api/useFetchOrganizationCandidateProfilesCustomFields';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';
import ApiRequestParamsSection from '../components/apiSettings/ApiRequestParamsSection';
import {
  ApiKeyInfoSection,
  ApiKeyUsageSection,
  ExistingApiKeySection,
  IssueApiKeySection,
} from '../components/apiSettings/ApiKeySections';
import McpSettingsSection from '../components/apiSettings/McpSettingsSection';
import { mapSharedToCustomFields } from '../utils/customFields';
import type { ApiIntegrationTabType } from './EditPage';

export default function ApiSettings() {
  const navigate = useNavigate({ from: '/agent/settings' });
  const search = useSearch({ from: '/agent/settings/' });
  const integrationTab: ApiIntegrationTabType = search.st ?? 'api';

  const setIntegrationTab = (tab: ApiIntegrationTabType) => {
    navigate({
      to: '/agent/settings',
      search: (prev) => ({ ...prev, st: tab }),
      replace: true,
    });
  };

  const { data: organizationApiKey, refetch: refetchOrganizationApiKey } =
    useFetchOrganizationApiKey();
  const [apiKey, setApiKey] = useState<OrganizationApiKey | null>(null);
  const [isJustIssued, setIsJustIssued] = useState(false);
  const { mutateAsync: postOrganizationApiKey } = usePostOrganizationApiKey();
  const {
    data: candidateProfileCustomFields,
    refetch: refetchOrganizationCandidateProfilesCustomFields,
  } = useFetchOrganizationCandidateProfilesCustomFields();
  const candidateProfileCustomFieldsForView = useMemo(
    () => mapSharedToCustomFields(candidateProfileCustomFields),
    [candidateProfileCustomFields]
  );

  useEffect(() => {
    // 新規発行直後は、完全なキーを保持するため上書きしない
    if (organizationApiKey && !isJustIssued) {
      setApiKey(organizationApiKey);
    }
  }, [organizationApiKey, isJustIssued]);

  const handleDeleteApiKey = async () => {
    try {
      const result = await refetchOrganizationApiKey();
      setApiKey(result.data || null);
      setIsJustIssued(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleIssueApiKey = async () => {
    try {
      const result = await postOrganizationApiKey();
      setIsJustIssued(true);
      setApiKey(result);
      showSuccessToast('APIキーを発行しました。');
    } catch (error) {
      showErrorToast('APIキーの発行に失敗しました。');
    }
  };

  const handleUpdateCustomFields = async () => {
    refetchOrganizationCandidateProfilesCustomFields();
  };

  const handleCloseApiKeyInfo = () => {
    setIsJustIssued(false);
  };

  return (
    <>
      <OrganizationSettingsTabs />
      <div className="mt-4 space-y-8">
        <div className="rounded-sm bg-white p-1 shadow-layer1">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setIntegrationTab('api')}
              className={`flex-1 cursor-pointer rounded border-0 py-sm text-heading-xxs ${
                integrationTab === 'api' ? 'bg-primary_lighten text-primary' : 'bg-white text-sub'
              }`}
            >
              APIで連携する
            </button>
            <button
              type="button"
              onClick={() => setIntegrationTab('ai')}
              className={`flex-1 cursor-pointer rounded border-0 py-sm text-heading-xxs ${
                integrationTab === 'ai' ? 'bg-primary_lighten text-primary' : 'bg-white text-sub'
              }`}
            >
              AIと連携する
            </button>
          </div>
        </div>
        {integrationTab === 'ai' && (
          <div>
            <McpSettingsSection />
          </div>
        )}
        {integrationTab === 'api' && (
          <>
            {!apiKey && (
              <div>
                <IssueApiKeySection onIssue={handleIssueApiKey} />
              </div>
            )}
            {isJustIssued && apiKey && (
              <div>
                <ApiKeyInfoSection apiKey={apiKey} onClose={handleCloseApiKeyInfo} />
              </div>
            )}
            {!isJustIssued && apiKey && (
              <>
                <div>
                  <ExistingApiKeySection apiKey={apiKey} onDelete={handleDeleteApiKey} />
                </div>
                <div>
                  <ApiKeyUsageSection />
                </div>
                <div>
                  <ApiRequestParamsSection
                    candidateProfileCustomFields={candidateProfileCustomFieldsForView}
                    onUpdate={handleUpdateCustomFields}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
